// Firestore storage for newsletter signups (founder direction 2026-09-03).
// Firebase is imported lazily so the map/analytics path pays no bundle cost -
// the SDK chunk loads only when someone actually submits an email.
//
// Client-side writes are spam-able by design; server-side validation via a
// Cloud Function is the robust version and is recorded as a follow-up in
// PROGRESS.md. The Firestore rules (firestore.rules) are the interim guard.

export interface SignupPayload {
  email: string;
  source: string;
  project?: string;
}

const requireEnv = (key: string): string => {
  const value = import.meta.env[key];
  if (typeof value !== 'string' || value.length === 0) {
    // Fail loudly: a build without the Firebase config must produce a visible
    // signup error, never a silent success that stored nothing (CLAUDE.md §8).
    throw new Error(`[newsletter] ${key} is not set — signup cannot be stored`);
  }
  return value;
};

export const submitSignup = async ({ email, source, project }: SignupPayload): Promise<void> => {
  const config = {
    apiKey: requireEnv('VITE_FIREBASE_API_KEY'),
    authDomain: requireEnv('VITE_FIREBASE_AUTH_DOMAIN'),
    projectId: requireEnv('VITE_FIREBASE_PROJECT_ID'),
    appId: requireEnv('VITE_FIREBASE_APP_ID'),
  };

  const [
    { initializeApp, getApps, getApp },
    { getFirestore, collection, addDoc, serverTimestamp },
  ] = await Promise.all([import('firebase/app'), import('firebase/firestore')]);

  const app = getApps().length > 0 ? getApp() : initializeApp(config);
  const db = getFirestore(app);

  await addDoc(collection(db, 'newsletter_signups'), {
    email,
    source,
    // The rules require a string; an absent project name is stored as ''.
    project: project ?? '',
    createdAt: serverTimestamp(),
    userAgent: navigator.userAgent,
  });

  if (import.meta.env.DEV) {
    // Dev-only echo of what was stored, for local verification.
    console.log('[newsletter] signup stored', { email, source, project });
  }
};
